import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { adminCustomers } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const formatCurrency = (value) => {
  if (value == null || value === '') return '—';
  const num = parseFloat(value);
  if (isNaN(num)) return '—';
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const formatDate = (d) => {
  if (!d) return '—';
  const parsed = new Date(d);
  if (isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString();
};

export default function CustomerList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSalesRep = user?.role === 'sales_rep';

  // Filter state
  const [search, setSearch]     = useState('');
  const [status, setStatus]     = useState('');
  const [repId, setRepId]       = useState('');

  // Data state
  const [customers, setCustomers] = useState([]);
  const [reps, setReps]           = useState([]);

  // UI state
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [successMsg, setSuccessMsg]   = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  // Selection state
  const [selected, setSelected] = useState(new Set());

  // Auto-dismiss success alert
  const successTimerRef = useRef(null);
  const showSuccess = useCallback((msg) => {
    setSuccessMsg(msg);
    if (successTimerRef.current) clearTimeout(successTimerRef.current);
    successTimerRef.current = setTimeout(() => setSuccessMsg(''), 4000);
  }, []);

  // Load reps once on mount
  useEffect(() => {
    adminCustomers
      .reps()
      .then((res) => {
        if (res.data?.success) setReps(res.data.data || []);
      })
      .catch(() => {
        // Non-fatal; rep filter will just be empty
      });
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  // Load customers whenever filters change
  const loadCustomers = useCallback(() => {
    setLoading(true);
    setError('');
    const params = {};
    if (search.trim()) params.search = search.trim();
    if (status)        params.status = status;
    if (repId)         params.repId  = repId;

    adminCustomers
      .list(params)
      .then((res) => {
        if (res.data?.success) {
          setCustomers(res.data.data || []);
        } else {
          setError('Failed to load customers.');
        }
      })
      .catch(() => {
        setError('An error occurred while loading customers.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [search, status, repId]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  // Checkbox helpers
  const allSelected =
    customers.length > 0 && customers.every((c) => selected.has(c.customerID));
  const someSelected = selected.size > 0;

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(customers.map((c) => c.customerID)));
    }
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Bulk action
  const handleBulk = async (action) => {
    if (!someSelected) return;
    setBulkLoading(true);
    setError('');
    try {
      await adminCustomers.bulk({ action, ids: Array.from(selected) });
      showSuccess(
        action === 'approve'
          ? 'Selected customers approved.'
          : 'Selected customers suspended.'
      );
      setSelected(new Set());
      loadCustomers();
    } catch {
      setError(`Failed to ${action} selected customers.`);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleRowClick = (e, id) => {
    // Don't navigate when clicking the checkbox cell
    if (e.target.type === 'checkbox') return;
    navigate(`/admin/customers/${id}`);
  };

  return (
    <div className="container-fluid py-4">
      {/* Page header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="mb-0">Customers</h4>
        <button
          className="btn btn-primary btn-sm d-flex align-items-center gap-1"
          onClick={() => navigate('/admin/customers/new')}
        >
          <Plus size={16} />
          New Customer
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError('')}
            aria-label="Close"
          />
        </div>
      )}
      {successMsg && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {successMsg}
          <button
            type="button"
            className="btn-close"
            onClick={() => setSuccessMsg('')}
            aria-label="Close"
          />
        </div>
      )}

      {/* Scoped accounts banner for sales reps */}
      {isSalesRep && (
        <div className="alert alert-info d-flex align-items-center gap-2 py-2 mb-3">
          <span className="badge bg-info text-dark me-1">Your Accounts</span>
          Showing your {customers.length} assigned account{customers.length !== 1 ? 's' : ''}.
        </div>
      )}

      {/* Filter bar */}
      <div className="card mb-3">
        <div className="card-body py-2">
          <div className="row g-2 align-items-end">
            <div className="col-12 col-md-5">
              <label className="form-label small mb-1">Search</label>
              <div className="input-group input-group-sm">
                <span className="input-group-text">
                  <Search size={14} />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Company name or email…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>

            <div className="col-6 col-md-3">
              <label className="form-label small mb-1">Status</label>
              <select
                className="form-select form-select-sm"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            {!isSalesRep && (
              <div className="col-6 col-md-3">
                <label className="form-label small mb-1">Assigned Rep</label>
                <select
                  className="form-select form-select-sm"
                  value={repId}
                  onChange={(e) => setRepId(e.target.value)}
                >
                  <option value="">All Reps</option>
                  {reps.map((r) => (
                    <option key={r.id} value={r.id}>
                      {`${r.firstName} ${r.lastName}`}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="col-12 col-md-1 d-flex align-items-end">
              <button
                className="btn btn-outline-secondary btn-sm w-100 d-flex align-items-center justify-content-center gap-1"
                onClick={loadCustomers}
                title="Refresh"
              >
                <RefreshCw size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      <div className="d-flex align-items-center gap-2 mb-2">
        <button
          className="btn btn-success btn-sm d-flex align-items-center gap-1"
          disabled={!someSelected || bulkLoading}
          onClick={() => handleBulk('approve')}
        >
          <CheckCircle size={14} />
          Approve Selected
        </button>
        <button
          className="btn btn-warning btn-sm d-flex align-items-center gap-1"
          disabled={!someSelected || bulkLoading}
          onClick={() => handleBulk('suspend')}
        >
          <XCircle size={14} />
          Suspend Selected
        </button>
        {someSelected && (
          <span className="text-muted small">{selected.size} selected</span>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : customers.length === 0 ? (
        <div className="text-center text-muted py-5">
          No customers found matching your filters.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover table-striped align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: '1%' }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allSelected}
                    onChange={toggleAll}
                    aria-label="Select all"
                  />
                </th>
                <th>Company Name</th>
                <th>Contact</th>
                <th>Status</th>
                <th>Payment Type</th>
                <th>Credit Cap</th>
                <th>Balance</th>
                <th>Rep</th>
                <th>Last Order</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr
                  key={c.customerID}
                  onClick={(e) => handleRowClick(e, c.customerID)}
                  style={{ cursor: 'pointer' }}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={selected.has(c.customerID)}
                      onChange={() => toggleOne(c.customerID)}
                      aria-label={`Select ${c.companyName}`}
                    />
                  </td>

                  <td className="fw-semibold">{c.companyName || '—'}</td>

                  <td>
                    <div>{c.firstName} {c.lastName}</div>
                    <div className="text-muted small">{c.email}</div>
                    {c.phone && (
                      <div className="text-muted small">{c.phone}</div>
                    )}
                  </td>

                  <td>
                    {c.isApproved ? (
                      <span className="badge bg-success">Active</span>
                    ) : (
                      <span className="badge bg-warning text-dark">Pending</span>
                    )}
                  </td>

                  <td className="text-capitalize">{c.paymentType || '—'}</td>

                  <td>{formatCurrency(c.creditCap)}</td>

                  <td>{formatCurrency(c.outstandingBalance)}</td>

                  <td>{c.repName || '—'}</td>

                  <td>{formatDate(c.lastOrderDate)}</td>

                  <td onClick={(e) => e.stopPropagation()}>
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => navigate(`/admin/customers/${c.customerID}`)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
